import React, { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { PLACES, Place, CateringItem, CateringOffer } from '../data/smaken';
import { CATERING } from '../data/catering';
import { slugify } from '../utils/image';

/**
 * The live platform: every partner, their profile, menu sections and products.
 *
 * Admin writes here; the public catering pages read from here. State is
 * persisted locally and synced between open tabs, so a change saved in the
 * admin shows on the public site immediately. The shape is a plain JSON
 * document (export/import below) so it can move to a hosted database without
 * changing any page.
 */

export interface Partner {
  place: Place;
  /** Unpublished partners are invisible on the public site. */
  published: boolean;
  /** The partner's own menu headings, in display order. */
  sections: string[];
  items: CateringItem[];
}
interface PlatformDocument {
  version: 1;
  partners: Partner[];
  updatedAt: number;
}
type Direction = -1 | 1;
interface PlatformContextType {
  partners: Partner[];
  updatedAt: number;
  storageError: string | null;
  /** Published partners' visible items, in each partner's menu order. */
  allCatering: CateringOffer[];
  partnerFor: (slug: string) => Partner | undefined;
  cateringFor: (slug: string) => CateringOffer[];
  addPartner: () => string;
  updatePartnerProfile: (slug: string, place: Place) => string;
  setPublished: (slug: string, published: boolean) => void;
  deletePartner: (slug: string) => void;
  addSection: (slug: string, name: string) => void;
  renameSection: (slug: string, from: string, to: string) => void;
  moveSection: (slug: string, name: string, dir: Direction) => void;
  deleteSection: (slug: string, name: string) => void;
  saveItem: (slug: string, item: CateringItem) => void;
  deleteItem: (slug: string, id: string) => void;
  duplicateItem: (slug: string, id: string) => void;
  moveItem: (slug: string, id: string, dir: Direction) => void;
  setItemHidden: (slug: string, id: string, hidden: boolean) => void;
  exportJson: () => string;
  importJson: (json: string) => boolean;
  resetPlatform: () => void;
}
const STORAGE_KEY = 'smaken-platform-v1';
const CHANNEL = 'smaken-platform';
const PlatformContext = createContext<PlatformContextType | undefined>(undefined);
export function PlatformProvider({
  children
}: {
  children: ReactNode;
}) {
  const [doc, setDoc] = useState<PlatformDocument>(loadDocument);
  const [storageError, setStorageError] = useState<string | null>(null);
  const fromRemote = useRef(false);
  const channel = useRef<BroadcastChannel | null>(null);

  // Persist + tell other tabs. Skipped when the change itself came from another tab.
  useEffect(() => {
    if (fromRemote.current) {
      fromRemote.current = false;
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(doc));
      setStorageError(null);
      channel.current?.postMessage(doc.updatedAt);
    } catch {
      setStorageError('Lagringen är full. Ta bort eller byt ut några stora bilder och spara igen.');
    }
  }, [doc]);

  // Live sync from other tabs (admin in one, public site in another).
  useEffect(() => {
    const pull = () => {
      const next = readStored();
      if (!next) return;
      setDoc(current => {
        if (next.updatedAt === current.updatedAt) return current;
        fromRemote.current = true;
        return next;
      });
    };
    const onStorage = (e: StorageEvent) => e.key === STORAGE_KEY && pull();
    window.addEventListener('storage', onStorage);
    if (typeof BroadcastChannel !== 'undefined') {
      channel.current = new BroadcastChannel(CHANNEL);
      channel.current.onmessage = pull;
    }
    return () => {
      window.removeEventListener('storage', onStorage);
      channel.current?.close();
      channel.current = null;
    };
  }, []);
  const value = useMemo<PlatformContextType>(() => {
    const commit = (update: (partners: Partner[]) => Partner[]) => setDoc(current => ({
      ...current,
      partners: update(current.partners),
      updatedAt: Date.now()
    }));
    const edit = (slug: string, update: (partner: Partner) => Partner) => commit(partners => partners.map(p => p.place.slug === slug ? update(p) : p));
    const uniqueSlug = (base: string, partners: Partner[], except?: string) => {
      const root = slugify(base) || 'verksamhet';
      let candidate = root;
      let n = 2;
      while (partners.some(p => p.place.slug === candidate && p.place.slug !== except)) {
        candidate = `${root}-${n++}`;
      }
      return candidate;
    };
    const toOffers = (partner: Partner): CateringOffer[] => orderedItems(partner).filter(item => !item.hidden).map(item => ({
      ...item,
      placeSlug: partner.place.slug,
      placeName: partner.place.name,
      placeCity: String(partner.place.city)
    }));
    const published = doc.partners.filter(p => p.published);
    const allCatering = published.flatMap(toOffers);
    return {
      partners: doc.partners,
      updatedAt: doc.updatedAt,
      storageError,
      allCatering,
      partnerFor: slug => doc.partners.find(p => p.place.slug === slug),
      cateringFor: slug => allCatering.filter(o => o.placeSlug === slug),
      addPartner: () => {
        const slug = uniqueSlug('ny-verksamhet', doc.partners);
        commit(partners => [...partners, blankPartner(slug)]);
        return slug;
      },
      updatePartnerProfile: (slug, place) => {
        const nextSlug = uniqueSlug(place.slug || place.name, doc.partners, slug);
        edit(slug, p => ({
          ...p,
          place: {
            ...place,
            slug: nextSlug
          }
        }));
        return nextSlug;
      },
      setPublished: (slug, isPublished) => edit(slug, p => ({
        ...p,
        published: isPublished
      })),
      deletePartner: slug => commit(partners => partners.filter(p => p.place.slug !== slug)),
      addSection: (slug, name) => {
        const clean = name.trim();
        if (!clean) return;
        edit(slug, p => p.sections.includes(clean) ? p : {
          ...p,
          sections: [...p.sections, clean]
        });
      },
      renameSection: (slug, from, to) => {
        const clean = to.trim();
        if (!clean || clean === from) return;
        edit(slug, p => ({
          ...p,
          sections: p.sections.includes(clean) ? p.sections.filter(s => s !== from) : p.sections.map(s => s === from ? clean : s),
          items: p.items.map(i => i.section === from ? {
            ...i,
            section: clean
          } : i)
        }));
      },
      moveSection: (slug, name, dir) => edit(slug, p => ({
        ...p,
        sections: swap(p.sections, p.sections.indexOf(name), dir)
      })),
      deleteSection: (slug, name) => edit(slug, p => p.items.some(i => i.section === name) ? p : {
        ...p,
        sections: p.sections.filter(s => s !== name)
      }),
      saveItem: (slug, item) => edit(slug, p => {
        const sections = p.sections.includes(item.section) ? p.sections : [...p.sections, item.section];
        const exists = p.items.some(i => i.id === item.id);
        return {
          ...p,
          sections,
          items: exists ? p.items.map(i => i.id === item.id ? item : i) : [...p.items, item]
        };
      }),
      deleteItem: (slug, id) => edit(slug, p => ({
        ...p,
        items: p.items.filter(i => i.id !== id)
      })),
      duplicateItem: (slug, id) => edit(slug, p => {
        const index = p.items.findIndex(i => i.id === id);
        if (index < 0) return p;
        const copy = {
          ...p.items[index],
          id: newItemId(slug),
          name: `${p.items[index].name} (kopia)`,
          hidden: true
        };
        const items = [...p.items];
        items.splice(index + 1, 0, copy);
        return {
          ...p,
          items
        };
      }),
      moveItem: (slug, id, dir) => edit(slug, p => {
        const index = p.items.findIndex(i => i.id === id);
        if (index < 0) return p;
        const section = p.items[index].section;
        // Swap with the nearest neighbour in the same section, skipping other sections.
        let target = index + dir;
        while (target >= 0 && target < p.items.length && p.items[target].section !== section) target += dir;
        if (target < 0 || target >= p.items.length) return p;
        const items = [...p.items];
        [items[index], items[target]] = [items[target], items[index]];
        return {
          ...p,
          items
        };
      }),
      setItemHidden: (slug, id, hidden) => edit(slug, p => ({
        ...p,
        items: p.items.map(i => i.id === id ? {
          ...i,
          hidden
        } : i)
      })),
      exportJson: () => JSON.stringify(doc, null, 2),
      importJson: json => {
        try {
          const parsed = JSON.parse(json) as PlatformDocument;
          if (!Array.isArray(parsed.partners)) return false;
          setDoc({
            version: 1,
            partners: parsed.partners,
            updatedAt: Date.now()
          });
          return true;
        } catch {
          return false;
        }
      },
      resetPlatform: () => setDoc(seedDocument())
    };
  }, [doc, storageError]);
  return <PlatformContext.Provider value={value} data-id="element-759" data-mp="39o534eqie877">{children}</PlatformContext.Provider>;
}
export function usePlatform() {
  const context = useContext(PlatformContext);
  if (!context) throw new Error('usePlatform must be used within a PlatformProvider');
  return context;
}

/* --------------------------------- Helpers --------------------------------- */

export const newItemId = (slug: string) => `${slug.slice(0, 6)}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

/** Items in the partner's section order; unknown sections fall to the end. */
export function orderedItems(partner: Partner): CateringItem[] {
  const rank = (section: string) => {
    const i = partner.sections.indexOf(section);
    return i < 0 ? partner.sections.length : i;
  };
  return partner.items.map((item, index) => ({
    item,
    index
  })).sort((a, b) => rank(a.item.section) - rank(b.item.section) || a.index - b.index).map(({
    item
  }) => item);
}
function swap<T>(list: T[], index: number, dir: Direction): T[] {
  const target = index + dir;
  if (index < 0 || target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
function blankPartner(slug: string): Partner {
  return {
    published: false,
    sections: ['Frukost', 'Lunch', 'Fika'],
    items: [],
    place: {
      slug,
      name: 'Ny verksamhet',
      city: 'Växjö',
      kind: 'ata',
      tags: [],
      blurb: '',
      description: [],
      weLike: [],
      image: '',
      gallery: [],
      address: '',
      hours: [],
      price: 2,
      action: 'bestall'
    }
  };
}
function seedDocument(): PlatformDocument {
  return {
    version: 1,
    updatedAt: Date.now(),
    partners: PLACES.map(place => {
      const items = CATERING[place.slug] ?? [];
      return {
        place,
        published: true,
        sections: [...new Set(items.map(i => i.section))],
        items
      };
    })
  };
}
function readStored(): PlatformDocument | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PlatformDocument;
    return Array.isArray(parsed.partners) ? parsed : null;
  } catch {
    return null;
  }
}
function loadDocument(): PlatformDocument {
  return readStored() ?? seedDocument();
}